import React, { useEffect, useRef, useState } from 'react'
import { Col, Descriptions, Form, Modal as ModalAntd, Row } from 'antd'
import { Formik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import * as Yup from 'yup'

import { Id, Request, RequestStandardSharing } from 'src/types'

import { ApplicationState } from 'src/store'
import { Button, ButtonTitled, Modal } from 'src/components/Ant'
import { calcRestPercentage } from 'src/core/hooks/use-calc-rest-percentage'
import { useT } from 'src/core/hooks/use-translation'
import {
  getInitialValues,
  getValidationSchema
} from 'src/core/forms/request/sharing'
import {
  resetSaveFullRequest,
  saveRequestStandardAssignments
} from 'src/core/store/ducks/requests/actions'
import useStandardResource from 'src/core/hooks/use-standard-resource'

import FormFields from '../FormFields'
import { WrapAlert, WrapDescriptions, WrapProgress } from './styles'

type Props = {
  request: Request
  toggle(): void
  visible: boolean
}

const initialValues = getInitialValues()

function ModalForm({ request, toggle, visible }: Props): JSX.Element {
  const dispatch = useDispatch()
  const { requests } = useSelector((state: ApplicationState) => ({
    requests: state.requests
  }))

  const [idEditing, setIdEditing] = useState<Id>()
  const [saving, setSaving] = useState<boolean>(false)

  const refFormik =
    useRef<Formik<{ request_standard_sharings: RequestStandardSharing[] }>>(
      null
    )

  const costCenterStandard = useStandardResource('cost_centers')
  const contractStandard = useStandardResource('contracts')

  const { t } = useT()

  useEffect(() => {
    if (visible) {
      refFormik.current.resetForm({
        request_standard_sharings:
          request.request_standard_assignments.request_standard_sharings.map(
            item => ({
              ...item,
              contract_id: item.contract_id.toString(),
              cost_center_id: item.cost_center_id.toString()
            })
          )
      })
    }
  }, [request, visible])

  useEffect(() => {
    if (requests.saveFullRequestLoading !== saving) {
      setSaving(requests.saveFullRequestLoading)

      if (!requests.saveFullRequestLoading && !requests.saveFullRequestError) {
        toggle()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests.saveFullRequestLoading])

  return (
    <Modal
      cancelButtonProps={{ disabled: saving }}
      closable={!saving}
      forceRender
      maskClosable={!saving}
      okButtonProps={{ loading: saving }}
      onCancel={() => {
        toggle()
        dispatch(resetSaveFullRequest())
      }}
      onOk={() => refFormik.current.submitForm()}
      title="web.assignment.sharing.form.title-edit"
      visible={visible}
      width={820}
    >
      <Formik
        ref={refFormik}
        initialValues={{
          request_standard_sharings: []
        }}
        onSubmit={values => {
          const percentage = values.request_standard_sharings
            .filter(({ active }) => active)
            .reduce((acc, cur) => acc + cur.percentage, 0)

          if (percentage !== 100) {
            ModalAntd.error({
              title: t('web.assignment.sharing.incomplete')
            })
            return
          }

          dispatch(saveRequestStandardAssignments(values))
        }}
        validationSchema={Yup.object().shape({
          request_standard_sharings: Yup.array().of(getValidationSchema())
        })}
        render={formikProps => {
          const restPercentage = calcRestPercentage(
            formikProps.values.request_standard_sharings.filter(
              calcItem => calcItem.active
            )
          )

          return (
            <Form onSubmit={formikProps.handleSubmit}>
              {formikProps.values.request_standard_sharings
                .filter(sharingItem => sharingItem.active)
                .map(sharingItem => {
                  const enableDescription =
                    (!!sharingItem.tmp_id &&
                      idEditing !== sharingItem.tmp_id) ||
                    (!!sharingItem.request_standard_sharing_id &&
                      idEditing !== sharingItem.request_standard_sharing_id)

                  return (
                    <Row
                      align="middle"
                      key={
                        sharingItem.request_standard_sharing_id ||
                        sharingItem.tmp_id
                      }
                      justify="space-between"
                      type="flex"
                    >
                      <Col span={20}>
                        {enableDescription && (
                          <WrapDescriptions layout="vertical" bordered>
                            <Descriptions.Item
                              label={t(
                                'web.assignment.sharing.form.cost-center.title'
                              )}
                            >
                              {sharingItem.cost_center.name}
                            </Descriptions.Item>
                            <Descriptions.Item
                              label={t(
                                'web.assignment.sharing.form.contract.title'
                              )}
                            >
                              {sharingItem.contract.name}
                            </Descriptions.Item>
                            <Descriptions.Item
                              label={t(
                                'web.assignment.sharing.form.percentage.title'
                              )}
                            >
                              {sharingItem.percentage}%
                            </Descriptions.Item>
                          </WrapDescriptions>
                        )}
                        {!enableDescription && (
                          <FormFields
                            organization_id={request.organization_id}
                            formikProps={formikProps}
                            index={formikProps.values.request_standard_sharings.findIndex(
                              indexItem => {
                                if (sharingItem.request_standard_sharing_id) {
                                  return (
                                    sharingItem.request_standard_sharing_id ===
                                    indexItem.request_standard_sharing_id
                                  )
                                }

                                return sharingItem.tmp_id === indexItem.tmp_id
                              }
                            )}
                          />
                        )}
                      </Col>
                      <ButtonTitled
                        icon={enableDescription ? 'edit' : 'check'}
                        onClick={() =>
                          setIdEditing(
                            enableDescription
                              ? sharingItem.tmp_id
                                ? sharingItem.tmp_id
                                : sharingItem.request_standard_sharing_id
                              : null
                          )
                        }
                        shape="circle"
                        tooltip={`common.actions.${
                          enableDescription ? 'edit' : 'add'
                        }`}
                      />
                      <ButtonTitled
                        icon="delete"
                        onClick={() => {
                          if (sharingItem.tmp_id) {
                            formikProps.resetForm({
                              request_standard_sharings:
                                formikProps.values.request_standard_sharings.filter(
                                  deleteItem =>
                                    deleteItem.tmp_id !== sharingItem.tmp_id
                                )
                            })

                            return
                          }

                          formikProps.resetForm({
                            request_standard_sharings:
                              formikProps.values.request_standard_sharings.map(
                                deleteItem => ({
                                  ...deleteItem,
                                  active:
                                    deleteItem.request_standard_sharing_id ===
                                    sharingItem.request_standard_sharing_id
                                      ? false
                                      : deleteItem.active
                                })
                              )
                          })
                        }}
                        shape="circle"
                        tooltip="common.actions.remove"
                        type="danger"
                      />
                    </Row>
                  )
                })}
              <WrapProgress
                percent={100 - restPercentage}
                size="small"
                status={restPercentage > 0 ? 'exception' : null}
              />
              {restPercentage > 0 && (
                <Form.Item>
                  <Button
                    block
                    icon="plus"
                    onClick={() => {
                      const tmp_id = Math.random()

                      setIdEditing(tmp_id)

                      formikProps.resetForm({
                        request_standard_sharings: [
                          ...formikProps.values.request_standard_sharings,
                          {
                            ...initialValues,
                            tmp_id,
                            contract: contractStandard,
                            contract_id: contractStandard
                              ? contractStandard.contract_id.toString()
                              : null,
                            cost_center: costCenterStandard,
                            cost_center_id: costCenterStandard
                              ? costCenterStandard.cost_center_id.toString()
                              : null,
                            percentage: restPercentage
                          }
                        ]
                      })
                    }}
                    title="common.actions.add"
                    type="primary"
                  />
                </Form.Item>
              )}
              {requests.saveFullRequestError && (
                <WrapAlert
                  message={requests.saveFullRequestError}
                  showIcon
                  type="error"
                />
              )}
            </Form>
          )
        }}
      />
    </Modal>
  )
}

export default ModalForm
